import type { CheckboxTreeContextType } from './CheckboxTreeContext.ts';
import type { CheckboxTreeNode } from './types.ts';
import { times } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { Skeleton, SkeletonBlock, SkeletonText } from '@_/ui';
import { CheckboxTreeContext } from './CheckboxTreeContext.ts';
import { CheckboxTreeItem } from './CheckboxTreeItem.tsx';

export interface CheckboxTreeProps<TValue> extends Pick<
  CheckboxTreeContextType<TValue>,
  'disabled' | 'onChange' | 'renderLabel'
> {
  loading?: boolean;
  nodes: Array<CheckboxTreeNode<TValue>>;
  value: CheckboxTreeContextType<TValue>['selectedIds'];
}

export function CheckboxTree<TValue>({
  disabled,
  loading,
  nodes,
  onChange,
  renderLabel,
  value
}: CheckboxTreeProps<TValue>) {
  return (
    <CheckboxTreeContext
      value={useMemo(
        () => ({
          disabled,
          onChange,
          renderLabel,
          selectedIds: value || []
        }),
        [disabled, onChange, renderLabel, value]
      )}
    >
      {loading ? (
        <Skeleton className="flex flex-col gap-y-4">
          {times(3, i => (
            <div key={i} className="flex items-center">
              <SkeletonBlock className="size-4 shrink" />
              <SkeletonText className="ml-3 max-w-60 grow" />
            </div>
          ))}
        </Skeleton>
      ) : (
        <div className="flex flex-col gap-y-4">
          {nodes.map(node => (
            <CheckboxTreeItem key={node.id} node={node} />
          ))}
        </div>
      )}
    </CheckboxTreeContext>
  );
}
